import { Injectable } from '@angular/core';
import { Moment } from 'moment';
import { Observable } from 'rxjs';

import { AppService } from '../app.service';
import { Brand } from '../models/common/brand.model';
import { Category } from '../models/common/category.model';
import { Color } from '../models/common/color.model';
import { Ids } from '../models/common/ids.model';
import { OrderDetailsSchema } from '../models/common/order-details-schema.model';
import { OrdersListFilters } from '../models/common/orders-list-filters.model';
import { OrdersListSchema } from '../models/common/orders-list-schema.model';
import { PhotographerBase } from '../models/common/photographer-base.model';
import { PhotographerStatus } from '../models/common/photographer-status.model';
import { Photographer } from '../models/common/photographer.models';
import { PublishShots } from '../models/common/publish-shots.model';
import { SchemaResult } from '../models/common/schema-result.model';
import { ShotType } from '../models/common/shot-type.model';
import { SizeGroup } from '../models/common/size-group.model';
import { Style } from '../models/common/style.model';
import { UpdateShotStatus } from '../models/common/update-shot-status.model';
import { BrandIdCategoryId } from '../models/company/brand-id-category-id.model';
import { CompanyDashboardSchema } from '../models/company/company-dashboard.models';
import { CompanyOrderDetails } from '../models/company/company-order-details.model';
import { CompanyOrderListItem } from '../models/company/company-order-list-item.model';
import { CompanyProductsSchema } from '../models/company/company-products-schema.model';
import { CompanyStyleDetailsSchema } from '../models/company/company-style-details-schema.models';
import { CompanyStyle } from '../models/company/company-style.model';
import { CompanyStylesFiltersIds } from '../models/company/company-styles-filters-ids.model';
import { ReshootProduct } from '../models/company/reshoot-product.model';
import { Template } from '../models/company/template.models';
import { UpdatePhotographer } from '../models/company/update-photographer.models';
import { UpdateTemplate } from '../models/company/update-template.models';
import { ShotStatusEnum } from '../shared/models/shot-status.enum';
import { StudiosHttpClientService } from '../shared/services/studios-http-client.service';
import { SuggestedSeason } from '../models/company/suggested-season.type';
import { MediaFormat } from '../models/common/media-format.type';
import { AspectRatio } from '../models/common/aspect-ratio.type';
import { CodifiedUsed } from '../models/common/codified-used.type';


@Injectable()
export class CompanyService {

  private baseUrl: string;

  constructor(
    private appService: AppService,
    private studiosHttpClient: StudiosHttpClientService
  ) {
    this.baseUrl = this.appService.utilsUrls.apiUrl;
  }

  public getPhotographers(paginationPage: number, paginationSize: number, search: string, statusIds: Array<Ids> = [], isActive?: boolean, loaderId?: string, photographerIds?: Array<Ids>,): Observable<Photographer[]> {
    const url = this.baseUrl + '/Company/GetPhotographers';
    return this.studiosHttpClient.post<Photographer[]>(url, { statusIds, photographerIds }, {
      params: {
        currentUserId: this.appService.user.userId.toString(),
        companyId: this.appService.companyId.toString(),
        paginationPage: paginationPage.toString(),
        paginationSize: paginationSize.toString(),
        search: search || '',
        isActive: isActive ? 'true' : 'false'
      }
    }, loaderId);
  }

  public getBrands(loaderId?: string, brandIds?: Array<Ids>): Observable<Array<Brand>> {
    const url = this.baseUrl + '/Company/GetBrands';
    return this.studiosHttpClient.post<Array<Brand>>(url, brandIds || [], {
      params: { currentUserId: this.appService.user.userId.toString(), companyId: this.appService.companyId.toString() }
    }, loaderId);
  }

  public getCategories(brandId: number, loaderId?: string): Observable<Category[]> {
    const url = this.baseUrl + '/Company/GetCategories';
    return this.studiosHttpClient.get<Category[]>(url, {
      params: { brandId: brandId.toString() }
    }, loaderId);
  }

  public GetCategoriesByIds(categoryIds: Array<Ids>, loaderId?: string): Observable<Array<Category>> {
    const url = this.baseUrl + '/Company/GetCategoriesByIds';
    return this.studiosHttpClient.post<Array<Category>>(url, categoryIds, {}, loaderId);
  }

  public async getCategoriesAsync(brandId: number): Promise<Array<Category>> {
    const data = (await fetch(`${this.baseUrl}/Company/GetCategories?brandId=${brandId}`)).json();
    return data;
  }

  public getCategoryStyles(brandId: number, categoryId: number, paginationPage: number, paginationSize: number, search: string, loaderId?: string): Observable<Style[]> {
    search = search || '';
    const url = this.baseUrl + '/Company/GetCategoryStyles';
    return this.studiosHttpClient.get<Style[]>(url, {
      params: {
        brandId: brandId.toString(),
        categoryId: categoryId.toString(),
        paginationPage: paginationPage.toString(),
        paginationSize: paginationSize.toString(),
        search
      }
    }, loaderId);
  }

  public getSizeGroups(brandId: number, loaderId?: string): Observable<SizeGroup[]> {
    const url = this.baseUrl + '/Company/GetSizeGroups';
    return this.studiosHttpClient.get<SizeGroup[]>(url, {
      params: { brandId: brandId.toString() }
    }, loaderId);
  }

  public getCompanyDashboard(loaderId?: string): Observable<CompanyDashboardSchema> {
    const url = this.baseUrl + '/Company/GetCompanyDashboard';
    return this.studiosHttpClient.get<CompanyDashboardSchema>(url, {
      params: { currentUserId: this.appService.user.userId.toString(), companyId: this.appService.companyId.toString() }
    }, loaderId);
  }

  public getOrders(paginationPage: number, paginationSize: number, search: string, filters: OrdersListFilters, loaderId?: string): Observable<OrdersListSchema<CompanyOrderListItem>> {
    const url = this.baseUrl + '/Company/GetCompanyOrders';
    return this.studiosHttpClient.post<OrdersListSchema<CompanyOrderListItem>>(url, filters, {
      params: {
        currentUserId: this.appService.user.userId.toString(),
        companyId: this.appService.companyId.toString(),
        paginationPage: paginationPage.toString(),
        paginationSize: paginationSize.toString(),
        search: search || ''
      }
    }, loaderId);
  }

  public getCompanyOrderDetails(orderId: number, loaderId?: string): Observable<OrderDetailsSchema<CompanyOrderDetails>> {
    const url = this.baseUrl + '/Company/GetCompanyOrderDetails';
    return this.studiosHttpClient.get<OrderDetailsSchema<CompanyOrderDetails>>(url, {
      params: { companyId: this.appService.companyId.toString(), orderId: orderId.toString() }
    }, loaderId);
  }

  public getCompanyProducts(paginationPage: number, paginationSize: number, loaderId?: string): Observable<CompanyProductsSchema> {
    const url = this.baseUrl + '/Company/GetProducts';
    return this.studiosHttpClient.get<CompanyProductsSchema>(url, {
      params: {
        companyId: this.appService.companyId.toString(),
        paginationPage: paginationPage.toString(),
        paginationSize: paginationSize.toString()
      }
    }, loaderId);
  }

  public getCompanyStyles(paginationPage: number, paginationSize: number, search: string, notAssigned: boolean, brandIdCategoryIds: BrandIdCategoryId[], photographersIds: Ids[], warehouseIds: Ids[], loaderId?: string): Observable<SchemaResult<CompanyStyle>> {
    const url = this.baseUrl + '/Company/GetCompanyStyles';
    const body = new CompanyStylesFiltersIds({
      BrandIdCategoryIds: brandIdCategoryIds,
      PhotographersIds: photographersIds,
      WarehousesIds: warehouseIds
    });
    return this.studiosHttpClient.post<SchemaResult<CompanyStyle>>(url, body, {
      params: {
        currentUserId: this.appService.user.userId.toString(),
        companyId: this.appService.companyId.toString(),
        paginationPage: paginationPage.toString(),
        paginationSize: paginationSize.toString(),
        search: search || '',
        notAssigned: notAssigned ? 'true' : 'false'
      }
    }, loaderId);
  }

  public updateShotStatus(shotId: number, statusId: ShotStatusEnum, comments?: string, loaderId?: string): Observable<number> {
    const url = this.baseUrl + '/Company/UpdateShotStatus';
    const body = new UpdateShotStatus(shotId, statusId, comments);
    return this.studiosHttpClient.post<number>(url, body, {
      params: { currentUserId: this.appService.user.userId.toString() }
    }, loaderId);
  }

  public getTemplates(paginationPage: number, paginationSize: number, brandIdsCategoryIds: BrandIdCategoryId[], loaderId?: string): Observable<Template[]> {
    const url = this.baseUrl + '/Company/GetTemplates';
    return this.studiosHttpClient.post<Template[]>(url, brandIdsCategoryIds, {
      params: {
        currentUserId: this.appService.user.userId.toString(),
        companyId: this.appService.companyId.toString(),
        paginationPage: paginationPage.toString(),
        paginationSize: paginationSize.toString()
      }
    }, loaderId);
  }

  public deleteTemplate(parentTemplateId: number, loaderId?: string): Observable<number> {
    const url = this.baseUrl + '/Company/DeleteTemplate';
    return this.studiosHttpClient.post<number>(url, null, {
      params: { currentUserId: this.appService.user.userId.toString(), companyId: this.appService.companyId.toString(), parentTemplateId: parentTemplateId.toString() }
    }, loaderId);
  }

  public updateTemplate(updateTemplate: UpdateTemplate, loaderId?: string): Observable<number> {
    const url = this.baseUrl + '/Company/UpdateTemplate';
    return this.studiosHttpClient.post<number>(url, updateTemplate, {
      params: { currentUserId: this.appService.user.userId.toString(), companyId: this.appService.companyId.toString() }
    }, loaderId);
  }

  public publishShots(orderId: number, modelId: number, colorId: number, momentPublicationDate: Moment, loaderId?: string): Observable<number> {
    const url = this.baseUrl + '/Company/PublishShots';
    const publicationDate = momentPublicationDate.utc(true).toISOString();
    const body = new PublishShots(orderId, modelId, colorId, publicationDate);
    return this.studiosHttpClient.post<number>(url, body, {
      params: { currentUserId: this.appService.user.userId.toString() }
    }, loaderId);
  }

  public getColorsByModel(modelId: number, paginationPage: number, paginationSize: number, search: string, loaderId?: string): Observable<Color[]> {
    const url = this.baseUrl + '/Company/GetColorsByModel';
    return this.studiosHttpClient.get<Color[]>(url, {
      params: {
        currentUserId: this.appService.user.userId.toString(),
        modelId: modelId.toString(),
        paginationPage: paginationPage.toString(),
        paginationSize: paginationSize.toString(),
        search: search || ''
      }
    }, loaderId);
  }

  public getShotTypes(paginationPage: number, paginationSize: number, search: string, loaderId?: string): Observable<ShotType[]> {
    const url = this.baseUrl + '/Company/GetShotTypes';
    return this.studiosHttpClient.get<ShotType[]>(url, {
      params: {
        currentUserId: this.appService.user.userId.toString(),
        paginationPage: paginationPage.toString(),
        paginationSize: paginationSize.toString(),
        search: search || ''
      }
    }, loaderId);
  }

  public updatePhotographer(updatePhotographer: UpdatePhotographer, loaderId?: string): Observable<number> {
    const url = this.baseUrl + '/Company/UpdatePhotographer';
    return this.studiosHttpClient.post<number>(url, updatePhotographer, {
      params: { currentUserId: this.appService.user.userId.toString(), companyId: this.appService.companyId.toString() }
    }, loaderId);
  }

  public getPhotographerStatus(loaderId?: string, statusIds?: Array<Ids>): Observable<Array<PhotographerStatus>> {
    const url = this.baseUrl + '/Company/GetPhotographerStatus';
    return this.studiosHttpClient.post<Array<PhotographerStatus>>(url, statusIds || [], {
      params: { currentUserId: this.appService.user.userId.toString() }
    }, loaderId);
  }

  public getCompanyStyleDetails(modelId: number, paginationPage: number, paginationSize: number, search: string, loaderId?: string): Observable<CompanyStyleDetailsSchema> {
    const url = this.baseUrl + '/Company/GetCompanyStyleDetails';
    return this.studiosHttpClient.get<CompanyStyleDetailsSchema>(url, {
      params: {
        currentUserId: this.appService.user.userId.toString(),
        modelId: modelId.toString(),
        paginationPage: paginationPage.toString(),
        paginationSize: paginationSize.toString(),
        search: search || ''
      }
    }, loaderId);
  }

  public getPhotographersStatusRequested(loaderId?: string): Observable<PhotographerBase[]> {
    const url = this.baseUrl + '/Company/GetPhotographersStatusRequested';
    return this.studiosHttpClient.get<PhotographerBase[]>(url, {
      params: {
        currentUserId: this.appService.user.userId.toString(),
        companyId: this.appService.companyId.toString()
      }
    }, loaderId);
  }

  public getStyleTemplate(brandId: number, modelId: number, colorId: number, sizeGroupId?: number, loaderId?: string): Observable<Template> {
    const url = this.baseUrl + '/Company/GetStyleTemplate';
    return this.studiosHttpClient.get<Template>(url, {
      params: {
        currentUserId: this.appService.user.userId.toString(),
        companyId: this.appService.companyId.toString(),
        brandId: brandId.toString(),
        modelId: modelId.toString(),
        colorId: colorId.toString(),
        sizeGroupId: !!sizeGroupId || sizeGroupId === 0 ? sizeGroupId.toString() : ''
      }
    }, loaderId);
  }

  public reshootProduct(modelId: number, colorId?: number, sizeGroupId?: number, loaderId?: string): Observable<number> {
    const url = this.baseUrl + '/Company/ReshootProduct';
    const body = new ReshootProduct({
      ModelId: modelId,
      ColorId: colorId >= 0 ? colorId : null,
      SizeGroupId: sizeGroupId >= 0 ? sizeGroupId : null
    });
    return this.studiosHttpClient.post<number>(url, body, {
      params: {
        currentUserId: this.appService.user.userId.toString(),
        companyId: this.appService.companyId.toString()
      }
    }, loaderId);
  }

  public GetSuggestedSeason(loaderId?: string, suggestedSeasonIds?: Array<Ids>): Observable<Array<SuggestedSeason>> {
    const url = this.baseUrl + '/Company/GetSuggestedSeasons';
    return this.studiosHttpClient.post<Array<SuggestedSeason>>(url, suggestedSeasonIds || [], {}, loaderId);
  }

  public getMediaTypes(loaderId?: string): Observable<Array<MediaFormat>> {
    const url = this.baseUrl + '/Company/GetMediaTypes';
    return this.studiosHttpClient.get<Array<MediaFormat>>(url, {}, loaderId);
  }

  public getAspectRatios(loaderId?: string): Observable<Array<AspectRatio>> {
    const url = this.baseUrl + '/Company/GetAspectRatios';
    return this.studiosHttpClient.get<Array<AspectRatio>>(url, {}, loaderId);
  }

  public getCodifiedUseds(paginationPage: number, paginationSize: number, search: string, loaderId?: string): Observable<Array<CodifiedUsed>> {
    const url = this.baseUrl + '/Company/GetCodifiedUseds';
    return this.studiosHttpClient.get<Array<CodifiedUsed>>(url, {
      params: {
        paginationPage: paginationPage.toString(),
        paginationSize: paginationSize.toString(),
        search: search || ''
      }
    }, loaderId);
  }
}
